/**
 * Blending-session commands — the workspace's record of beers blended.
 *
 * Structurally the water-profile four: create (optionally from a template),
 * update, delete, restore. A blend is its own document, not a field on a
 * batch: one session mixes several beers, some of which may never have been
 * batches in this workspace.
 *
 * `blend.setStage` is the fifth, and it is a command of its own rather than an
 * update because moving a blend on is one tap that writes two fields, and the
 * page should not have to compose a patch to make it (the batch's
 * `setStatus`, for the batch's reason).
 *
 * The last three are the blend's cellar tasks (PLAN-UX-PASS E1) — the batch's
 * three, with a blend at the other end. See the section header below.
 */

import {
  type BlendDoc,
  blankBlendDoc,
  emptyBlendStageDates,
  isBlendDatedStage,
} from '../../document/blend';
import { blendContains } from '../../document/blend-graph';
import { defineCommand, inverseCommand } from '../definition';
import { commandError } from '../envelope';
import { quoted, transition } from '../format';
import {
  dropBlend,
  indexOfId,
  insertAt,
  mergeDefined,
  priorValues,
  putBlend,
  removeAt,
  withBlend,
} from '../helpers';
import * as p from '../payloads';

/**
 * Create a blending session, optionally by duplicating an existing one.
 *
 * A copy starts at `planned` with no dates, however far along the session it
 * came from is. A duplicate is the beers and the numbers again — next year's
 * gueuze, the same barrel refilled — and it has not been blended, conditioned
 * or packaged; carrying last year's days over would be the record claiming
 * something that never happened. The rule is here rather than in the client's
 * duplicate action because a create synced from another device arrives without
 * ever having met one.
 */
export const blendCreate = defineCommand({
  type: 'blend.create',
  payload: p.blendCreatePayloadSchema,
  apply: (state, command) => {
    const { blendId, name, from } = command.payload;
    if (state.blends[blendId] !== undefined) {
      return {
        ok: false,
        error: commandError('conflict', `A blend with id "${blendId}" already exists.`, {
          commandId: command.commandId,
          commandType: command.type,
        }),
      };
    }
    const doc: BlendDoc =
      from === undefined
        ? blankBlendDoc({ id: blendId, name, timestamp: command.timestamp })
        : {
            ...from,
            id: blendId,
            name,
            // After the spread, deliberately: see this command's header. The
            // readings go with them (D-BS12) — a hydrometer reading is
            // something that happened to the original beer rather than one of
            // the numbers that describe the recipe for it, and a copy has not
            // been blended yet, let alone measured.
            stage: 'planned',
            stageDates: emptyBlendStageDates(),
            readings: [],
            // And the chores go with them (PLAN-UX-PASS E1): a scheduled task
            // is a day in the original beer's life, so carrying last year's
            // "pull the oak cubes" into next year's gueuze would put a date in
            // the past on a session that has not been blended.
            cellarTasks: [],
            createdAt: command.timestamp,
            updatedAt: command.timestamp,
          };
    return { ok: true, state: putBlend(state, doc) };
  },
  invert: (_state, command) =>
    inverseCommand(
      command,
      'blend.delete',
      { blendId: command.payload.blendId },
      command.timestamp,
    ),
  summarize: (_state, command) => `Create blend ${quoted(command.payload.name)}`,
});

/** Change fields of a blending session. */
export const blendUpdate = defineCommand({
  type: 'blend.update',
  payload: p.blendUpdatePayloadSchema,
  apply: (state, command) => {
    const blend = state.blends[command.payload.blendId];
    if (blend === undefined) {
      return {
        ok: false,
        error: commandError('not-found', `No blend with id "${command.payload.blendId}".`, {
          commandId: command.commandId,
          commandType: command.type,
        }),
      };
    }
    // The document refuses a loop, not the page: an edit synced from another
    // device arrives here without ever having met a picker (D-BL9).
    const looping = (command.payload.changes.components ?? []).some(
      (component) =>
        component.blendId !== null && blendContains(state, blend.id, component.blendId),
    );
    if (looping) {
      return {
        ok: false,
        error: commandError('conflict', 'A blend cannot contain itself.', {
          commandId: command.commandId,
          commandType: command.type,
        }),
      };
    }
    return {
      ok: true,
      state: putBlend(state, {
        ...mergeDefined(blend, command.payload.changes),
        updatedAt: command.timestamp,
      }),
    };
  },
  invert: (state, command) => {
    const blend = state.blends[command.payload.blendId];
    return blend === undefined
      ? null
      : inverseCommand(
          command,
          'blend.update',
          {
            blendId: blend.id,
            changes: priorValues(blend, command.payload.changes),
          },
          blend.updatedAt,
        );
  },
  summarize: (state, command) => {
    const blend = state.blends[command.payload.blendId];
    const keys = Object.keys(command.payload.changes).sort().join(', ');
    return `Update blend ${quoted(blend?.name ?? command.payload.blendId)}${
      keys === '' ? '' : ` (${keys})`
    }`;
  },
});

/** Move a blending session to another stage, stamping the day if the command names one. */
export const blendSetStage = defineCommand({
  type: 'blend.setStage',
  payload: p.blendSetStagePayloadSchema,
  apply: (state, command) => {
    const blend = state.blends[command.payload.blendId];
    if (blend === undefined) {
      return {
        ok: false,
        error: commandError('not-found', `No blend with id "${command.payload.blendId}".`, {
          commandId: command.commandId,
          commandType: command.type,
        }),
      };
    }
    const { stage, on } = command.payload;
    // Only the stage being reached is stamped, and only when the command
    // carries a day: moving backwards leaves every date alone, because a
    // blender who tapped Packaged a day early has not un-packaged anything
    // (D-BS4). The payload's refine is what makes the narrowing total.
    const stageDates =
      on === undefined || !isBlendDatedStage(stage)
        ? blend.stageDates
        : { ...blend.stageDates, [stage]: on };
    return {
      ok: true,
      state: putBlend(state, { ...blend, stage, stageDates, updatedAt: command.timestamp }),
    };
  },
  // An update rather than a `setStage` back: the forward command may have
  // written a date, and a bare stage cannot un-write one. Restoring both
  // fields from the prior document undoes either half exactly.
  invert: (state, command) => {
    const blend = state.blends[command.payload.blendId];
    return blend === undefined
      ? null
      : inverseCommand(
          command,
          'blend.update',
          {
            blendId: blend.id,
            changes: { stage: blend.stage, stageDates: blend.stageDates },
          },
          blend.updatedAt,
        );
  },
  summarize: (state, command) => {
    const prior = state.blends[command.payload.blendId];
    return prior === undefined
      ? `Set blend stage to ${command.payload.stage}`
      : `Move ${quoted(prior.name)} ${transition(prior.stage, command.payload.stage)}`;
  },
});

/** Delete a blending session. */
export const blendDelete = defineCommand({
  type: 'blend.delete',
  payload: p.blendDeletePayloadSchema,
  apply: (state, command) => {
    const blend = state.blends[command.payload.blendId];
    if (blend === undefined) {
      return {
        ok: false,
        error: commandError('not-found', `No blend with id "${command.payload.blendId}".`, {
          commandId: command.commandId,
          commandType: command.type,
        }),
      };
    }
    return { ok: true, state: dropBlend(state, command.payload.blendId) };
  },
  invert: (state, command) => {
    const blend = state.blends[command.payload.blendId];
    return blend === undefined
      ? null
      : inverseCommand(command, 'blend.restore', { blend }, blend.updatedAt);
  },
  summarize: (state, command) => {
    const blend = state.blends[command.payload.blendId];
    return `Delete blend ${quoted(blend?.name ?? command.payload.blendId)}`;
  },
});

/** Insert a blending session verbatim — an upsert, and the inverse of `blend.delete`. */
export const blendRestore = defineCommand({
  type: 'blend.restore',
  payload: p.blendRestorePayloadSchema,
  apply: (state, command) => ({ ok: true, state: putBlend(state, command.payload.blend) }),
  invert: (state, command) => {
    const prior = state.blends[command.payload.blend.id];
    return prior === undefined
      ? inverseCommand(
          command,
          'blend.delete',
          { blendId: command.payload.blend.id },
          command.timestamp,
        )
      : inverseCommand(command, 'blend.restore', { blend: prior }, prior.updatedAt);
  },
  summarize: (_state, command) => `Restore blend ${quoted(command.payload.blend.name)}`,
});

// ---------------------------------------------------------------------------
// Cellar (PLAN-UX-PASS E1)
// ---------------------------------------------------------------------------

/**
 * The three chore commands, `definitions/batch.ts`' three with a blend in
 * place of the batch.
 *
 * A lambic blender's cellar runs on dated actions nothing derives — pull the
 * oak cubes, rack off the fruit, taste it in a month — and until these existed
 * a workspace of blends could not put one on any day. The batch's list is the
 * same list (D-BS5, and see `blendDocFieldsSchema.cellarTasks`), so these are
 * its commands rather than a blend's own take on them: same payload shapes,
 * same `mergeDefined` patch, same add/remove inverses carrying the position
 * back.
 *
 * Nothing here derives an event from a blend's stage dates. That park stands
 * (`PLAN-BLEND-STAGES.md:600`); a hand-scheduled chore is a different thing.
 */

/** Append (or insert) a cellar task on a blend. */
export const blendAddCellarTask = defineCommand({
  type: 'blend.addCellarTask',
  payload: p.blendAddCellarTaskPayloadSchema,
  apply: (state, command) =>
    withBlend(state, command, command.payload.blendId, (blend) => {
      const { task, index } = command.payload;
      if (indexOfId(blend.cellarTasks, 'taskId', task.taskId) !== -1) {
        return commandError(
          'conflict',
          `A cellar task with id "${task.taskId}" is already on this blend.`,
        );
      }
      return { ...blend, cellarTasks: insertAt(blend.cellarTasks, task, index) };
    }),
  invert: (state, command) => {
    const prior = state.blends[command.payload.blendId];
    return prior === undefined
      ? null
      : inverseCommand(
          command,
          'blend.removeCellarTask',
          { blendId: prior.id, taskId: command.payload.task.taskId },
          prior.updatedAt,
        );
  },
  summarize: (state, command) =>
    `Schedule ${quoted(command.payload.task.title)} on ${quoted(
      state.blends[command.payload.blendId]?.name ?? command.payload.blendId,
    )}`,
});

/** Patch one of a blend's cellar tasks. */
export const blendUpdateCellarTask = defineCommand({
  type: 'blend.updateCellarTask',
  payload: p.blendUpdateCellarTaskPayloadSchema,
  apply: (state, command) =>
    withBlend(state, command, command.payload.blendId, (blend) => {
      const index = indexOfId(blend.cellarTasks, 'taskId', command.payload.taskId);
      const current = blend.cellarTasks[index];
      if (current === undefined) {
        return commandError(
          'not-found',
          `No cellar task with id "${command.payload.taskId}" on this blend.`,
        );
      }
      const next = [...blend.cellarTasks];
      next[index] = mergeDefined(current, command.payload.changes);
      return { ...blend, cellarTasks: next };
    }),
  // `priorValues` cannot be used here: it answers for a whole document, and
  // what has to be restored is one row of a list. The batch's own
  // `updateCellarTask` builds the inverse the same way, key by key, and for the
  // same reason.
  invert: (state, command) => {
    const prior = state.blends[command.payload.blendId];
    const current =
      prior === undefined
        ? undefined
        : prior.cellarTasks[indexOfId(prior.cellarTasks, 'taskId', command.payload.taskId)];
    if (prior === undefined || current === undefined) return null;
    const changes: typeof command.payload.changes = {};
    for (const key of Object.keys(
      command.payload.changes,
    ) as (keyof typeof command.payload.changes)[]) {
      if (command.payload.changes[key] !== undefined) {
        (changes as Record<string, unknown>)[key] = current[key];
      }
    }
    return inverseCommand(
      command,
      'blend.updateCellarTask',
      { blendId: prior.id, taskId: command.payload.taskId, changes },
      prior.updatedAt,
    );
  },
  summarize: (_state, command) =>
    command.payload.changes.doneAt !== undefined
      ? command.payload.changes.doneAt === null
        ? 'Reopen a cellar task'
        : 'Mark a cellar task done'
      : 'Update a cellar task',
});

/** Remove one of a blend's cellar tasks. Invertible with its original position. */
export const blendRemoveCellarTask = defineCommand({
  type: 'blend.removeCellarTask',
  payload: p.blendRemoveCellarTaskPayloadSchema,
  apply: (state, command) =>
    withBlend(state, command, command.payload.blendId, (blend) => {
      const index = indexOfId(blend.cellarTasks, 'taskId', command.payload.taskId);
      if (index === -1) {
        return commandError(
          'not-found',
          `No cellar task with id "${command.payload.taskId}" on this blend.`,
        );
      }
      return { ...blend, cellarTasks: removeAt(blend.cellarTasks, index) };
    }),
  invert: (state, command) => {
    const prior = state.blends[command.payload.blendId];
    const index =
      prior === undefined ? -1 : indexOfId(prior.cellarTasks, 'taskId', command.payload.taskId);
    const task = prior?.cellarTasks[index];
    return prior === undefined || task === undefined
      ? null
      : inverseCommand(
          command,
          'blend.addCellarTask',
          { blendId: prior.id, task, index },
          prior.updatedAt,
        );
  },
  summarize: (state, command) => {
    const task = state.blends[command.payload.blendId]?.cellarTasks.find(
      (item) => item.taskId === command.payload.taskId,
    );
    return task === undefined ? 'Remove a cellar task' : `Remove ${quoted(task.title)}`;
  },
});
